import React from 'react';
import { useMutation } from '@apollo/client';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
  Fulfillment,
  MutationUpdateFulfillmentArgs,
  SellerOrder as SellerOrderType,
} from '@shopify-multiseller/graphql/partner/types';
import { Button } from '../../components/primitives';
import { FormInput } from '../../components/form';
import { SectionCard } from '../../components/SectionCard';
import { useNotification } from '../../components';
import { UPDATE_FULFILLMENT_MUTATION } from './queries';

const validationSchema = yup.object({
  trackingNumber: yup.string().required('Tracking number is required'),
  shippingCompany: yup.string(),
  trackingUrl: yup.string().url('Must be a valid URL'),
});

/**
 * "usps.com/track…" rather than the full query string.
 *
 * Carrier tracking hosts are almost always a service subdomain on the carrier's
 * domain — tools.usps.com, wwwapps.ups.com — and leading with it buried the
 * name the seller recognises. The last two labels are the carrier.
 */
const shortLink = (url: string): string => {
  try {
    const parsed = new URL(url);
    const labels = parsed.hostname.split('.');
    const host = labels.length > 2 ? labels.slice(-2).join('.') : parsed.hostname;
    const path = parsed.pathname.replace(/\/$/, '');
    return path && path !== '/' ? `${host}${path.slice(0, 14)}…` : host;
  } catch {
    return url;
  }
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <div className="mt-0.5 text-sm font-medium text-foreground">{children}</div>
  </div>
);

/**
 * The tracking a seller supplied when fulfilling, and a way to correct it.
 *
 * Tracking was previously write-once: it was captured on the fulfil screen and
 * displayed read-only forever after, so a mistyped number could only be fixed
 * by asking the shop. Editing goes through updateFulfillment, which changes the
 * tracking without touching the order's status.
 */
export const TrackingCard: React.FC<{
  orderId: string;
  fulfillment: Fulfillment;
  onUpdated: (_fulfillment: Fulfillment) => void;
}> = ({ orderId, fulfillment, onUpdated }) => {
  const [editing, setEditing] = React.useState(false);
  const notification = useNotification();
  const [updateFulfillment, { loading }] = useMutation<
    { updateFulfillment: SellerOrderType },
    MutationUpdateFulfillmentArgs
  >(UPDATE_FULFILLMENT_MUTATION);

  if (editing) {
    return (
      <SectionCard title="Tracking" testId="order-tracking-section">
        <Formik
          initialValues={{
            trackingNumber: fulfillment.trackingNumber ?? '',
            shippingCompany: fulfillment.shippingCarrier ?? '',
            trackingUrl: fulfillment.trackingUrl ?? '',
          }}
          validationSchema={validationSchema}
          validateOnBlur={false}
          onSubmit={(values) => {
            updateFulfillment({
              variables: {
                id: orderId,
                trackingInfo: {
                  trackingNumber: values.trackingNumber,
                  shippingCompany: values.shippingCompany || undefined,
                  trackingUrl: values.trackingUrl || undefined,
                },
              },
            })
              .then((result) => {
                const updated = result.data?.updateFulfillment?.fulfillment;
                if (updated) onUpdated(updated);
                setEditing(false);
                notification.success({ message: 'Tracking updated' });
              })
              .catch(() => {
                notification.error({ message: 'Tracking could not be updated' });
              });
          }}
        >
          {(props) => (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <FormInput
                  type="text"
                  label="Tracking number"
                  name="trackingNumber"
                  data-testid="tracking-edit-number"
                  autoComplete="off"
                  disabled={loading}
                />
                <FormInput
                  type="text"
                  label="Carrier"
                  name="shippingCompany"
                  data-testid="tracking-edit-carrier"
                  autoComplete="off"
                  disabled={loading}
                />
              </div>
              <FormInput
                type="text"
                label="Tracking link"
                name="trackingUrl"
                data-testid="tracking-edit-url"
                autoComplete="off"
                placeholder="https://"
                disabled={loading}
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={loading}
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  data-testid="tracking-save-button"
                  loading={loading}
                  disabled={loading}
                  onClick={() => props.handleSubmit()}
                >
                  Save
                </Button>
              </div>
            </div>
          )}
        </Formik>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Tracking"
      testId="order-tracking-section"
      trailing={
        <Button
          variant="outline"
          size="sm"
          data-testid="tracking-edit-button"
          onClick={() => setEditing(true)}
        >
          Edit
        </Button>
      }
    >
      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Tracking number">{fulfillment.trackingNumber || '—'}</Field>
        <Field label="Carrier">{fulfillment.shippingCarrier || '—'}</Field>
        <Field label="Tracking link">
          {fulfillment.trackingUrl ? (
            // The host, not the whole URL. A carrier tracking link is 90
            // characters of query string, which wrapped onto three lines and
            // ran past the edge of the card.
            <a
              href={fulfillment.trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={fulfillment.trackingUrl}
              className="block truncate text-primary hover:underline"
            >
              {shortLink(fulfillment.trackingUrl)}
            </a>
          ) : (
            '—'
          )}
        </Field>
      </div>
    </SectionCard>
  );
};
